import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { IInputField, ECommonValue } from '../../ui/Input/Input';
import { useInputs } from '../../ui/Input/useInputs';
import { RefHandle } from '../../../shared/RefHandle';
import { IHeaderLink } from '../../../shared/IHeaderLink';
import Button, { EButtonColor } from '../../ui/Button/Button';
import { EHeaderLinkInputs, getHeaderLinkInputs, formatHeaderLinkForInput, formatHeaderLinkForSubmit } from './form-utils';

interface IProps {
  sublink: IHeaderLink;
  index: number;
  onRemove: (index: number) => void;
}

const HeaderSublinkEdit: React.ForwardRefRenderFunction<RefHandle<IHeaderLink>, IProps> = ({ sublink, index, onRemove }, ref) => {
  const { createInput, submit } = useSublinkInputs(sublink);

  const getData = useCallback(async () => {
    const data = await submit();
    if (!data) return null;
    return formatHeaderLinkForSubmit({ ...sublink, ...data });
  }, [sublink, submit]);

  useImperativeHandle(ref, () => ({ getData }));

  return (
    <Draggable key={sublink.id || index} draggableId={sublink.id || `sub-${index}`} index={index}>
      {(provided) => (
        <tr ref={provided.innerRef} {...provided.draggableProps}>
          <td {...provided.dragHandleProps}>☰</td>
          <td>
            {createInput(EHeaderLinkInputs.text, { containerStyles: { marginBottom: 0 } })}
          </td>
          <td>
            {createInput(EHeaderLinkInputs.link, { containerStyles: { marginBottom: 0 } })}
          </td>
          <td>
            {createInput(EHeaderLinkInputs.isVisible, { 
              containerStyles: { marginBottom: 0 }, 
              options: [{ value: ECommonValue.YES, label: '' }]
            })}
          </td>
          <td>
            <Button onClick={() => onRemove(index)} color={EButtonColor.DANGER}>Remove</Button>
          </td>
        </tr>
      )}
    </Draggable>
  );
};

const useSublinkInputs = (data: IHeaderLink) => {
  const [inputs, setInputs] = useState<IInputField>(getHeaderLinkInputs(false));

  const formattedData = useMemo(() => formatHeaderLinkForInput(data), [data]);

  const { createInput, submit } = useInputs({
    data: formattedData,
    inputs,
    setInputs,
  });

  return { createInput, submit };
};

export default forwardRef(HeaderSublinkEdit);
